/**
 * Componente raíz: providers (Query + Auth + Workspace + Toast + Router) +
 * tree de rutas. Cada página lazy → chunk separado.
 */

import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { queryClient } from "./lib/queryClient";
import { Layout } from "./components/Layout";
import { ToastProvider } from "./components/Toast";
import { BackgroundTasksProvider } from "./components/BackgroundTasks";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { WorkspaceProvider } from "./workspace/WorkspaceContext";
import { ExecutionContextProvider } from "./workspace/ExecutionContext";
import { ThemeProvider } from "./theme/ThemeContext";

// Login NO lazy: primera ruta visible.
import { Login } from "./pages/Login";

const Dashboard = lazy(() =>
  import("./pages/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const Projects = lazy(() =>
  import("./pages/Projects").then((m) => ({ default: m.Projects })),
);
const ProjectDetail = lazy(() =>
  import("./pages/ProjectDetail").then((m) => ({ default: m.ProjectDetail })),
);
const HtDetail = lazy(() =>
  import("./pages/HtDetail").then((m) => ({ default: m.HtDetail })),
);
const RunDetail = lazy(() =>
  import("./pages/RunDetail").then((m) => ({ default: m.RunDetail })),
);
// Aprobaciones y Reports RETIRADOS de la UI (Carlos 2026-07-13): eje runtime
// sin uso (approvals=0 decisiones históricas; dashboards sobre validation_runs
// muertos desde mayo). Páginas en disco y endpoints backend INTACTOS (reversible).
// DevQuality RETIRADA de la UI (Carlos 2026-07-14): centralizada en el módulo
// Consistencia. La página y los endpoints /dev-quality quedan intactos.
// INTERNA: solo GERENCIA/ARQUITECTO/ADMIN (gate backend + menú).
const DevConsistency = lazy(() =>
  import("./pages/DevConsistency").then((m) => ({ default: m.DevConsistency })),
);
// Metrica de gerencia por servicio: ADMIN/ARQUITECTO/GERENCIA (gate backend + menu).
const ServiceMetrics = lazy(() =>
  import("./pages/ServiceMetrics").then((m) => ({ default: m.ServiceMetrics })),
);
const ConsistencyDashboard = lazy(() =>
  import("./pages/ConsistencyDashboard").then((m) => ({ default: m.ConsistencyDashboard })),
);
const ScheduledJobs = lazy(() =>
  import("./pages/ScheduledJobs").then((m) => ({ default: m.ScheduledJobs })),
);
const AiConfig = lazy(() =>
  import("./pages/AiConfig").then((m) => ({ default: m.AiConfig })),
);
const Diagnosis = lazy(() => import("./pages/Diagnosis"));
const ServiceDiagnosis = lazy(() => import("./pages/ServiceDiagnosis"));
const ServicePanorama = lazy(() => import("./pages/ServicePanorama"));
const PlanProjects = lazy(() => import("./pages/PlanProjects"));
const PlanProjectDetail = lazy(() => import("./pages/PlanProjectDetail"));
const RolesAdmin = lazy(() => import("./pages/RolesAdmin"));
const Avances = lazy(() => import("./pages/Avances"));
const Certifications = lazy(() => import("./pages/Certifications"));
const CertificationFolio = lazy(() => import("./pages/CertificationFolio"));
const MyCertifications = lazy(() => import("./pages/MyCertifications"));
const Activity = lazy(() => import("./pages/Activity"));
const Rules = lazy(() => import("./pages/Rules"));
const CanonicalPatterns = lazy(() => import("./pages/CanonicalPatterns"));
const Users = lazy(() =>
  import("./pages/Users").then((m) => ({ default: m.Users })),
);
const Workspaces = lazy(() =>
  import("./pages/Workspaces").then((m) => ({ default: m.Workspaces })),
);
const Environments = lazy(() =>
  import("./pages/Environments").then((m) => ({ default: m.Environments })),
);
const Profile = lazy(() =>
  import("./pages/Profile").then((m) => ({ default: m.Profile })),
);
const Notifications = lazy(() =>
  import("./pages/Notifications").then((m) => ({ default: m.Notifications })),
);
const Services = lazy(() =>
  import("./pages/Services").then((m) => ({ default: m.Services })),
);
const ServiceDetail = lazy(() =>
  import("./pages/ServiceDetail").then((m) => ({ default: m.ServiceDetail })),
);

function LoadingFallback() {
  return (
    <div className="flex h-full items-center justify-center text-sm text-slate-500">
      Cargando…
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
          <BackgroundTasksProvider>
          <AuthProvider>
            <ExecutionContextProvider>
            <WorkspaceProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/login" element={<Login />} />

                  <Route element={<ProtectedRoute />}>
                    <Route element={<Layout />}>
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="workspaces" element={<Workspaces />} />
                      <Route path="environments" element={<Environments />} />
                      <Route path="projects" element={<Projects />} />
                      <Route path="projects/:id" element={<ProjectDetail />} />
                      <Route path="projects/:id/hts/:htId" element={<HtDetail />} />
                      <Route path="runs/:runId" element={<RunDetail />} />
                      <Route path="dev-consistency" element={<DevConsistency />} />
                      <Route path="service-metrics" element={<ServiceMetrics />} />
                      <Route path="dev-consistency/:userId" element={<ConsistencyDashboard />} />
                      <Route path="my-consistency" element={<ConsistencyDashboard />} />
                      <Route path="diagnosis" element={<Diagnosis />} />
                      <Route path="service-diagnosis" element={<ServiceDiagnosis />} />
                      <Route path="service-panorama" element={<ServicePanorama />} />
                      <Route path="plan" element={<PlanProjects />} />
                      <Route path="plan/:id" element={<PlanProjectDetail />} />
                      <Route path="roles" element={<RolesAdmin />} />
                      <Route path="avances" element={<Avances />} />
                      <Route path="my-certifications" element={<MyCertifications />} />
                      <Route path="certifications" element={<Certifications />} />
                      {/* Folio de evidencia: destino del código CERT-... anexado a la HT. */}
                      <Route path="certifications/:code" element={<CertificationFolio />} />
                      <Route path="activity" element={<Activity />} />
                      <Route path="scheduled-jobs" element={<ScheduledJobs />} />
                      <Route path="ai-config" element={<AiConfig />} />
                      <Route path="users" element={<Users />} />
                      <Route path="rules" element={<Rules />} />
                      <Route path="canonical-patterns" element={<CanonicalPatterns />} />
                      <Route path="me/profile" element={<Profile />} />
                      <Route path="notifications" element={<Notifications />} />
                      <Route path="services" element={<Services />} />
                      <Route path="services/:id" element={<ServiceDetail />} />
                    </Route>
                  </Route>

                  <Route
                    path="*"
                    element={
                      <div className="flex h-screen items-center justify-center text-slate-500">
                        404 — ruta no encontrada
                      </div>
                    }
                  />
                </Routes>
              </Suspense>
            </WorkspaceProvider>
            </ExecutionContextProvider>
          </AuthProvider>
          </BackgroundTasksProvider>
          </ConfirmProvider>
        </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
